import React, { useEffect, useState } from "react"
import { Row, Col } from "reactstrap"
import { subDays } from "date-fns"
import axios from "axios"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { getCurrentUser, getPermission, canViewCallLogFeatures } from "../../../helpers/Utils.js"
import Paginations from "../../Pagination/index.jsx"
import Loader from "../../Loader/loader.js"
import CallLogRow from "./CallLogRow.js"
import CallLogReportFilter from "./callLogFilter.js"

const formatDate = date => {
  const d = new Date(date)
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${month}-${day}`
}

/** Parse YYYY-MM-DD as local calendar date (avoid UTC off-by-one). */
const parseLocalDate = ymd => {
  if (!ymd) return new Date()
  const parts = String(ymd).split("-").map(Number)
  if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) {
    return new Date(ymd)
  }
  return new Date(parts[0], parts[1] - 1, parts[2])
}

const CallLogsReport = () => {
  const canView = canViewCallLogFeatures()
  const today = new Date()
  const defaultStart = subDays(today, 30)

  const [rows, setRows] = useState([])
  const [allPages, setAllPages] = useState([])
  const [totals, setTotals] = useState(null)
  const [loader, setLoader] = useState(canView ? <Loader /> : "")
  const [error, setError] = useState("")
  const [counter, setCounter] = useState("")
  const [showMyReports, setShowMyReports] = useState(false)
  const [userIdFilter, setUserIdFilter] = useState("")
  const [directionFilter, setDirectionFilter] = useState("")
  const [callGapDays, setCallGapDays] = useState("")
  const [onlyLeads, setOnlyLeads] = useState(false)
  const [minDuration, setMinDuration] = useState("")
  const [maxDuration, setMaxDuration] = useState("")
  const [staffList, setStaffList] = useState([])
  const [filterModal, setFilterModal] = useState(false)
  const [showDaywise, setShowDaywise] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [startDate, setStartDate] = useState(formatDate(defaultStart))
  const [endDate, setEndDate] = useState(formatDate(today))
  const [dateFilter, setDateFilter] = useState([
    {
      startDate: defaultStart,
      endDate: today,
      key: "selection",
    },
  ])
  const fetchSeqRef = React.useRef(0)

  const authHeaders = () => ({
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${getCurrentUser()?.token || ""}`,
    },
  })

  const buildQuery = (page = 1) => {
    const perPage =
      localStorage.getItem("PerPage") !== null
        ? localStorage.getItem("PerPage")
        : "25"
    const params = new URLSearchParams({
      per_page: perPage,
      page: String(page),
      start_date: startDate,
      end_date: endDate,
    })
    if (userIdFilter) {
      params.set("user_id", userIdFilter)
    }
    if (directionFilter) {
      params.set("direction", directionFilter)
    }
    if (callGapDays) {
      params.set("call_gap_days", callGapDays)
    }
    if (onlyLeads) {
      params.set("only_leads", "1")
    }
    if (minDuration !== "" && Number(minDuration) >= 0) {
      params.set("min_duration", String(minDuration))
    }
    if (maxDuration !== "" && Number(maxDuration) >= 0) {
      params.set("max_duration", String(maxDuration))
    }
    return `${process.env.REACT_APP_API_URL}/${localStorage.getItem(
      "company_name"
    )}/call-logs-report?${params.toString()}`
  }

  const loadReport = (page = 1, signal) => {
    if (!canView) return
    const seq = ++fetchSeqRef.current
    setLoader(<Loader />)
    setError("")
    axios
      .get(buildQuery(page), { ...authHeaders(), signal })
      .then(res => {
        if (seq !== fetchSeqRef.current) return
        if (res.data.status) {
          const d = res.data.data
          setRows(d.data || [])
          setAllPages(d)
          setCounter(d.from)
          setTotals(d.totals || null)
          setLoader("")
        } else {
          setRows([])
          setTotals(null)
          setError(res.data.message || "No call logs found")
          setLoader("")
        }
      })
      .catch(err => {
        if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return
        }
        if (seq !== fetchSeqRef.current) return
        setRows([])
        setError("Failed to fetch call logs")
        setLoader("")
      })
  }

  const paginate = url => {
    if (!url || !canView) {
      return
    }
    const seq = ++fetchSeqRef.current
    setLoader(<Loader />)
    setRows([])
    axios
      .get(url, authHeaders())
      .then(res => {
        if (seq !== fetchSeqRef.current) return
        if (res.data.status) {
          const d = res.data.data
          setRows(d.data || [])
          setAllPages(d)
          setCounter(d.from)
          setTotals(d.totals || null)
          localStorage.setItem("current_page", d.current_page)
          setLoader("")
        } else {
          setError(res.data.message || "No call logs found")
          setLoader("")
        }
      })
      .catch(err => {
        if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return
        }
        if (seq !== fetchSeqRef.current) return
        setError("Failed to fetch call logs")
        setLoader("")
      })
  }

  const applyFilters = () => {
    let start = dateFilter[0].startDate
    let end = dateFilter[0].endDate || dateFilter[0].startDate
    const minAllowed = subDays(new Date(), 45)
    if (start < minAllowed) {
      start = minAllowed
    }
    const maxSpanStart = subDays(end, 45)
    if (start < maxSpanStart) {
      start = maxSpanStart
    }
    setStartDate(formatDate(start))
    setEndDate(formatDate(end))
    setDateFilter([
      {
        startDate: start,
        endDate: end,
        key: "selection",
      },
    ])
    setFilterModal(false)
  }

  const resetFilters = () => {
    const now = new Date()
    const from = subDays(now, 30)
    setStartDate(formatDate(from))
    setEndDate(formatDate(now))
    setDirectionFilter("")
    setCallGapDays("")
    setOnlyLeads(false)
    setMinDuration("")
    setMaxDuration("")
    setDateFilter([
      {
        startDate: from,
        endDate: now,
        key: "selection",
      },
    ])
    setUserIdFilter("")
    setShowMyReports(false)
    setSelectedIds([])
    setFilterModal(false)
  }

  const toggleMyReports = () => {
    if (showMyReports) {
      setUserIdFilter("")
      setShowMyReports(false)
    } else {
      setUserIdFilter(String(getCurrentUser().id))
      setShowMyReports(true)
    }
  }

  const selectedStaffName =
    userIdFilter &&
    staffList.find(u => String(u.id) === String(userIdFilter))?.name

  const loadStaffList = () => {
    if (!canView) return
    const cached = localStorage.getItem("AllUsers")
    if (cached) {
      try {
        const parsed = JSON.parse(cached)
        if (Array.isArray(parsed)) {
          setStaffList(parsed)
        }
      } catch (e) {
        // ignore invalid cache
      }
    }

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/${localStorage.getItem(
          "company_name"
        )}/user?per_page=500`,
        authHeaders()
      )
      .then(res => {
        if (res.data.status && res.data.data?.data) {
          setStaffList(res.data.data.data)
          localStorage.setItem("AllUsers", JSON.stringify(res.data.data.data))
        }
      })
      .catch(() => {})
  }

  const selectAllCheck = el => {
    const checkboxes = document.getElementsByName("chk")
    if (el.checked) {
      const ids = []
      for (let i = 0; i < checkboxes.length; i++) {
        checkboxes[i].checked = true
        ids.push(Number(checkboxes[i].value))
      }
      setSelectedIds(ids)
    } else {
      for (let i = 0; i < checkboxes.length; i++) {
        checkboxes[i].checked = false
      }
      setSelectedIds([])
    }
  }

  const toggleRowCheck = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => (prev.includes(id) ? prev : [...prev, id]))
    } else {
      setSelectedIds(prev => prev.filter(x => x !== id))
    }
  }

  const deleteExportFile = fileName => {
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/${localStorage.getItem(
          "company_name"
        )}/delete-call-logs-file`,
        { file_name: fileName },
        authHeaders()
      )
      .catch(() => {})
  }

  const exportReport = () => {
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/${localStorage.getItem(
          "company_name"
        )}/call-logs-export`,
        {
          ids: selectedIds,
          user_id: userIdFilter || undefined,
          start_date: startDate,
          end_date: endDate,
          direction: directionFilter || undefined,
          call_gap_days: callGapDays || undefined,
          only_leads: onlyLeads ? 1 : undefined,
          min_duration: minDuration || undefined,
          max_duration: maxDuration || undefined,
        },
        authHeaders()
      )
      .then(res => {
        if (res.data.status) {
          setSelectedIds([])
          window.location.href = res.data.file_url
          setTimeout(() => deleteExportFile(res.data.file_name), 30000)
        } else {
          toast.error(res.data.message || "Export failed", {
            position: "top-center",
            autoClose: 5000,
          })
        }
      })
      .catch(() => {
        toast.error("Export failed", { position: "top-center", autoClose: 5000 })
      })
  }

  useEffect(() => {
    if (!canView) return
    loadStaffList()
  }, [canView])

  useEffect(() => {
    if (!canView) return
    const controller = new AbortController()
    loadReport(1, controller.signal)
    return () => controller.abort()
  }, [canView, startDate, endDate, userIdFilter, directionFilter, callGapDays, onlyLeads, minDuration, maxDuration])

  const canExport =
    getPermission()?.lead_export?.lead_export?.view?.is_checked === "yes"

  if (!canView) {
    return (
      <div className="alert alert-warning mt-3">
        Call Logs Report is not available for this account.
      </div>
    )
  }

  return (
    <>
      <CallLogReportFilter
        open={filterModal}
        onClose={() => setFilterModal(false)}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        direction={directionFilter}
        setDirection={setDirectionFilter}
        staffUserId={userIdFilter}
        setStaffUserId={value => {
          setUserIdFilter(value)
          setShowMyReports(
            value !== "" && String(value) === String(getCurrentUser()?.id)
          )
        }}
        callGapDays={callGapDays}
        setCallGapDays={setCallGapDays}
        onlyLeads={onlyLeads}
        setOnlyLeads={setOnlyLeads}
        minDuration={minDuration}
        setMinDuration={setMinDuration}
        maxDuration={maxDuration}
        setMaxDuration={setMaxDuration}
        staffList={staffList}
        onApply={applyFilters}
      />

      <div className="page-title-box">
        <Row className="align-items-center">
          <Col md={4}>
            <h6 className="page-title">
              Call Logs Report{" "}
              <i className="fw-normal" style={{ fontSize: "0.85em" }}>
                (data available, max last 45 days)
              </i>
            </h6>
          </Col>
          <Col md={8}>
            <h6 className="page-title float-end">
              {startDate && endDate ? (
                <span className="me-2 badge bg-secondary text-dark fs-6 fw-bold">
                  From: {startDate} - To: {endDate}
                </span>
              ) : (
                ""
              )}
              {directionFilter ? (
                <span className="me-2 badge bg-info text-dark fs-6 fw-bold">
                  {directionFilter.replace("_", " ")}
                </span>
              ) : (
                ""
              )}
              {callGapDays ? (
                <span className="me-2 badge bg-warning text-dark fs-6 fw-bold">
                  Gap: last call ≥{callGapDays}d before end (ignores From date)
                </span>
              ) : (
                ""
              )}
              {onlyLeads ? (
                <span className="me-2 badge bg-success fs-6 fw-bold">
                  Only leads
                </span>
              ) : (
                ""
              )}
              {minDuration !== "" || maxDuration !== "" ? (
                <span className="me-2 badge bg-dark fs-6 fw-bold">
                  Duration:{" "}
                  {minDuration !== "" ? `${minDuration}s` : "0s"} –{" "}
                  {maxDuration !== "" ? `${maxDuration}s` : "∞"}
                </span>
              ) : (
                ""
              )}
              {selectedStaffName ? (
                <span className="me-2 badge bg-primary fs-6 fw-bold">
                  Staff: {selectedStaffName}
                </span>
              ) : (
                ""
              )}
              {/* {getCurrentUser()?.role_name !== "Executive" ? (
                <button
                  className="btn btn-primary submit__button me-2 mb-2"
                  type="button"
                  onClick={toggleMyReports}
                >
                  {showMyReports ? "All Reports" : "My Reports"}
                </button>
              ) : (
                ""
              )} */}
              <button
                className="btn btn-primary submit__button me-2 mb-2"
                type="button"
                onClick={() => {
                  setDateFilter([
                    {
                      startDate: startDate
                        ? parseLocalDate(startDate)
                        : defaultStart,
                      endDate: endDate ? parseLocalDate(endDate) : today,
                      key: "selection",
                    },
                  ])
                  setFilterModal(true)
                }}
              >
                <i className="fa fa-filter me-2" />
                Filter
              </button>
              <button
                className="btn btn-primary submit__button me-2 mb-2"
                type="button"
                onClick={resetFilters}
              >
                <i className="fa me-2" aria-hidden="true">
                  &#xf021;
                </i>
                Reset Filters
              </button>
              {canExport ? (
                <button
                  className="btn btn-primary submit__button me-2 mb-2"
                  type="button"
                  onClick={exportReport}
                >
                  Export
                </button>
              ) : (
                ""
              )}
            </h6>
          </Col>
        </Row>
      </div>

      {totals ? (
        <>
          {/* Summary strip */}
          <div
            className="mb-2"
            style={{
              background: "#fffbea",
              border: "1px solid #f0d060",
              borderRadius: "6px",
              padding: "10px 16px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "0",
            }}
          >
            {[
              { label: "Total Calls", value: totals.total_calls ?? 0 },
              { label: "Incoming", value: totals.incoming_calls ?? 0 },
              { label: "Outgoing", value: totals.outgoing_calls ?? 0 },
              { label: "Missed", value: totals.missed_calls ?? 0 },
              { label: "Not Picked", value: totals.not_picked_calls ?? 0 },
              { label: "Total Duration", value: totals.total_duration_formatted || "00:00:00" },
              { label: "Avg Duration/Day", value: totals.avg_duration_per_day_formatted || "0 min" },
              { label: "Avg Calls/Day", value: totals.avg_calls_per_day ?? 0 },
            ].map((item, idx, arr) => (
              <span
                key={item.label}
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "0 18px",
                  borderRight: idx < arr.length - 1 ? "1px solid #e0c840" : "none",
                  lineHeight: "1.3",
                }}
              >
                <span style={{ fontSize: "0.72em", color: "#7a6500", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
                  {item.label}
                </span>
                <span style={{ fontSize: "1em", fontWeight: 700, color: "#1a1a1a" }}>
                  {item.value}
                </span>
              </span>
            ))}

            {/* Day-wise toggle button */}
            {totals.daily_breakdown && totals.daily_breakdown.length > 0 && (
              <span style={{ marginLeft: "auto" }}>
                <button
                  onClick={() => setShowDaywise(p => !p)}
                  style={{
                    background: "none",
                    border: "1px solid #c9a800",
                    borderRadius: "4px",
                    padding: "3px 12px",
                    fontSize: "0.78em",
                    color: "#7a6500",
                    cursor: "pointer",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {showDaywise ? "▲ Hide Day-wise" : "▼ Day-wise Breakdown"}
                </button>
              </span>
            )}
          </div>

          {/* Collapsible day-wise table */}
          {showDaywise && totals.daily_breakdown && totals.daily_breakdown.length > 0 && (
            <div
              className="mb-3"
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "6px",
                overflow: "hidden",
                fontSize: "0.83em",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    <th style={{ padding: "7px 14px", textAlign: "left", color: "#495057", fontWeight: 700 }}>#</th>
                    <th style={{ padding: "7px 14px", textAlign: "left", color: "#495057", fontWeight: 700 }}>Date</th>
                    <th style={{ padding: "7px 14px", textAlign: "center", color: "#495057", fontWeight: 700 }}>Calls</th>
                    <th style={{ padding: "7px 14px", textAlign: "center", color: "#495057", fontWeight: 700 }}>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {totals.daily_breakdown.map((day, idx) => (
                    <tr
                      key={day.date}
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        background: idx % 2 === 0 ? "#fff" : "#fafafa",
                      }}
                    >
                      <td style={{ padding: "6px 14px", color: "#aaa" }}>{idx + 1}</td>
                      <td style={{ padding: "6px 14px", fontWeight: 600, color: "#343a40" }}>
                        {new Date(day.date).toLocaleDateString("en-IN", {
                          weekday: "short",
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td style={{ padding: "6px 14px", textAlign: "center", color: "#0056b3", fontWeight: 600 }}>{day.calls}</td>
                      <td style={{ padding: "6px 14px", textAlign: "center", color: "#155724", fontWeight: 600 }}>{day.duration_formatted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        ""
      )}

      <div className="card">
        <div className="card-body">
          <div className="lead_table table table-sm table-bordered table-responsive">
            <table style={{ width: "100%" }}>
              <thead>
                <tr className="static-header">
                  <th>
                    <input
                      type="checkbox"
                      className="p-0 d-inline-block"
                      onClick={e => selectAllCheck(e.target)}
                    />
                  </th>
                  <th>#</th>
                  <th>Staff</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Call Type</th>
                  <th>Duration</th>
                  <th>Date &amp; Time</th>
                  <th title="Days from this call date to report end date">
                    {callGapDays ? "Last call age" : "Days to end"}
                  </th>
                  <th>Lead</th>
                  <th>Recording</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((row, index) => (
                    <CallLogRow
                      key={row.id}
                      row={row}
                      index={index}
                      counter={counter}
                      selectedIds={selectedIds}
                      onToggleSelect={toggleRowCheck}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center py-4">
                      {loader ? "" : error || "No call logs found for this range"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {loader}
          {allPages?.from !== undefined ? (
            <Paginations
              data={allPages}
              paginate={paginate}
              perPage={() => loadReport(1)}
              local
            />
          ) : (
            ""
          )}
        </div>
      </div>
    </>
  )
}

export default CallLogsReport
